"use client"

import React, { useCallback } from "react"
import { useRouter } from "next/router"

import { useOrganization } from "@clerk/nextjs"
import { cn } from "@coval/ui/cn"
import { useMediaQuery } from "usehooks-ts"

import { usePageHeader } from "@/contexts/PageHeaderContext"
import { useSecondarySidebar } from "@/contexts/SecondarySidebarContext"
import { useQueryParams } from "@/hooks/useQueryParams"
import { isDashboardRoute } from "@/utils/isDashboardRoute"

import { useAppShellContributions } from "./AppShellContributionsContext"
import { AutoSecondarySidebar } from "./AutoSecondarySidebar"
import { DefaultPageHeader } from "./DefaultPageHeader"
import {
  EXPANDED_SIDEBAR_WIDTH_CLASS,
  SIDEBAR_COLLAPSE_BREAKPOINT,
} from "./sidebarDimensions"
import {
  SidebarNavCollapseToggle,
  useSidebarToggleShortcut,
} from "./SidebarNavCollapseToggle"
import { SidebarNavMobileMenu } from "./SidebarNavMobileMenu"
import { SidebarNavPanel } from "./SidebarNavPanel"
import { usePrimaryNavCollapsed } from "./usePrimaryNavCollapsed"
import { useSidebarPeek } from "./useSidebarPeek"

interface SidebarNavProps {
  children: React.ReactNode
}

const SidebarNav = ({ children }: SidebarNavProps) => {
  const { customPageHeader, noPaddingBottom, headerBackgroundClassName } =
    usePageHeader()
  // Below this width a docked navigation column would take a share of the
  // viewport the page cannot spare, so the panel moves into the overlay the
  // header's menu button opens and the content region gets the full width.
  const isCompact = useMediaQuery(
    `(max-width: ${SIDEBAR_COLLAPSE_BREAKPOINT}px)`,
  )
  // Where the panel can dock, the user can still hide it to give the page —
  // a span view, a labeling queue — the sidebar's width.
  const { isCollapsed, toggle: toggleCollapsed } = usePrimaryNavCollapsed()
  const isPanelDocked = !isCompact && !isCollapsed
  const isHiddenByUser = !isCompact && isCollapsed
  const {
    isPeeking,
    closePeek,
    suppressPeekUntilPointerLeaves,
    triggerProps: peekTriggerProps,
    surfaceProps: peekSurfaceProps,
  } = useSidebarPeek(isHiddenByUser)
  const handleToggleCollapsed = useCallback(() => {
    closePeek()
    toggleCollapsed()
  }, [closePeek, toggleCollapsed])
  // A click (`detail` > 0, unlike a keyboard press) hides the sidebar with the
  // pointer on the spot where the show toggle is about to appear.
  const handleHideFromPanel = useCallback(
    (event: React.MouseEvent) => {
      if (event.detail > 0) {
        suppressPeekUntilPointerLeaves()
      }
      handleToggleCollapsed()
    },
    [handleToggleCollapsed, suppressPeekUntilPointerLeaves],
  )
  useSidebarToggleShortcut(handleToggleCollapsed, !isCompact)
  const router = useRouter()
  const { secondarySidebar } = useSecondarySidebar()
  const { organization } = useOrganization()
  const appShellContributions = useAppShellContributions()
  const primaryNavWidthClass =
    appShellContributions.primaryNavWidthClass ?? EXPANDED_SIDEBAR_WIDTH_CLASS

  // Dashboard tabs logic
  const isDashboardPage = isDashboardRoute(router.pathname)

  const queryParams = useQueryParams()

  const getHref = useCallback(
    (baseHref: string) => {
      return queryParams.getHref(baseHref, organization?.slug || undefined)
    },
    [queryParams, organization?.slug],
  )

  // A header the page hands over as an empty fragment asks for no header row at
  // all; the shell's own controls then float over the content instead.
  const hasHeaderRow = !(
    customPageHeader !== null &&
    React.isValidElement(customPageHeader) &&
    customPageHeader.type === React.Fragment &&
    !(customPageHeader.props as { children?: unknown }).children
  )

  return (
    <div className="relative flex h-screen h-dvh">
      {/* With the sidebar hidden, its toggle stays pinned to the window on the
          panel's own padding — the exact spot it holds in the docked panel and
          the peek — so hiding, peeking, and pinning never move it. The header
          row keeps a gap for it rather than holding it, since the row's
          vertical center shifts with what a page puts in it. */}
      {isHiddenByUser && (
        <SidebarNavCollapseToggle
          isCollapsed
          onToggle={handleToggleCollapsed}
          peekTriggerProps={peekTriggerProps}
          className="absolute left-6 top-5 z-30"
        />
      )}

      {/* Primary Sidebar — docked only where there is room for it and the
          user has not hidden it. */}
      {isPanelDocked && (
        <SidebarNavPanel
          className={primaryNavWidthClass}
          headerAccessory={
            <SidebarNavCollapseToggle
              isCollapsed={false}
              onToggle={handleHideFromPanel}
            />
          }
        />
      )}

      {/* The hidden sidebar, peeked from its toggle: laid over the page in
          exactly the box the docked panel occupies, so every row is where the
          user expects it, with its own toggle there to pin it back. */}
      {isPeeking && (
        <div
          data-testid="sidebar-peek"
          className="fixed inset-y-0 left-0 z-40 flex border-r border-coval-border shadow-lg"
          {...peekSurfaceProps}
        >
          <SidebarNavPanel
            className={primaryNavWidthClass}
            onNavigate={closePeek}
            headerAccessory={
              <SidebarNavCollapseToggle
                isCollapsed
                onToggle={handleToggleCollapsed}
              />
            }
          />
        </div>
      )}

      {/* Secondary Sidebar (context-provided or auto-detected) */}
      {secondarySidebar || <AutoSecondarySidebar />}

      {/* Main Content Area — reflows for the docked Sofia panel. CoviMount
          sets --covi-dock-width / --covi-dock-transition on the document
          root when the panel is docked open; this margin squishes the content
          so the panel sits beside it instead of overlaying it. */}
      <div
        data-app-shell-main-region
        className="relative flex min-w-0 flex-1 flex-col overflow-hidden px-0"
        style={{
          marginRight: "var(--covi-dock-width, 0px)",
          transition: "var(--covi-dock-transition, none)",
        }}
      >
        {/* Inner region reflows for a drawer, which docks between this
            content and Sofia. It is a separate element from the Sofia reflow
            above so each dock keeps its own margin transition and the two
            resize independently: Sofia snaps its width in deliberately, while a
            drawer slides its space open in step with the panel's own 300ms
            slide — except mid-drag, where the Drawer primitive sets
            --drawer-dock-transition to none so the content tracks the pointer
            exactly. */}
        <div
          data-app-shell-content-region
          className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
          style={{
            marginRight: "var(--drawer-dock-width, 0px)",
            transition:
              "var(--drawer-dock-transition, margin-right 300ms ease-in-out)",
          }}
        >
          {!hasHeaderRow ? (
            <>
              {isCompact && (
                <SidebarNavMobileMenu className="absolute left-6 top-3 z-30" />
              )}
              {appShellContributions.renderHeaderTrailing?.({
                className: "absolute right-4 top-3 z-30",
              })}
            </>
          ) : (
            <header
              // `items-center` vertically centers the breadcrumb, the trailing
              // Sofia toggle, and every header's right-side actions on the
              // shared `min-h-17` baseline, so they line up with the sidebar's
              // `h-[28px]` selector (whose center also sits at the bar's
              // midline) regardless of each element's intrinsic height. This
              // supersedes the earlier `items-start` + `pt-5` scheme,
              // whose fixed top offset only truly centered content of
              // one specific height and left shorter/taller rows misaligned.
              className={cn(
                // `pr-6` is the 24px shell gutter: the trailing Sofia toggle
                // keeps the same margin from the window edge as the sidebar's
                // left and a table card's right. `pl-4` sets the breadcrumb
                // 16px in from a docked sidebar; with none docked, the leading
                // control sits on the same 24px gutter as the content below.
                "flex min-w-0 items-center justify-between bg-surface-contrast pr-6 pt-2 min-h-17",
                isPanelDocked ? "pl-4" : "pl-6",
                noPaddingBottom ? "pb-0" : "pb-2",
                headerBackgroundClassName,
              )}
            >
              {/* Where no panel is docked, the control that brings navigation
                  back leads the header row: the menu button where the panel
                  cannot dock, and the gap the pinned show toggle sits over
                  where the user hid it.
                  Where the panel cannot dock the viewport is narrow, often a
                  zoomed desktop, and a page's header can need more than one
                  line for its breadcrumb and actions. Its rows wrap there
                  instead of running under the trailing toggle, where the
                  content region would clip them out of reach. Each item wraps
                  at its content width rather than a zero `flex-1` basis, and
                  every row, `shrink-0` action groups included, can narrow to
                  its widest single control, so a line too short for a group
                  wraps the group's own controls. Columns stay single-line: a
                  wrapping column sizes its line to its widest item's full
                  width, which would stop a title from truncating. */}
              <div
                className={cn(
                  "flex min-w-0 flex-1 items-center gap-2",
                  isCompact &&
                    "[&_:is(div,nav):not(.flex-col)]:flex-wrap [&_:is(div,nav)]:basis-auto [&_:is(div,nav)]:shrink",
                )}
              >
                {isCompact && <SidebarNavMobileMenu className="shrink-0" />}
                {isHiddenByUser && (
                  <span
                    aria-hidden
                    className="w-[var(--height-control)] shrink-0"
                  />
                )}
                {customPageHeader !== null
                  ? customPageHeader
                  : (appShellContributions.renderDefaultPageHeader?.({
                      getHref,
                      isDashboardPage,
                    }) ?? <DefaultPageHeader getHref={getHref} />)}
                {/* The trailing toggle shares this region, which is as tall
                    as the page's header, so `self-end` puts it on the last
                    row: the action row of a wrapped header, and the same
                    line as everything else when the header fits on one. */}
                {appShellContributions.renderHeaderTrailing?.({
                  className: cn("ml-auto shrink-0", isCompact && "self-end"),
                })}
              </div>
            </header>
          )}

          {appShellContributions.renderStatusBanner?.({
            className: "mx-6 mb-2 shrink-0",
          })}

          <main
            className={cn(
              // The content area is the same contrast surface as the sidebar and
              // the header, so a page's table card reads as a card on it.
              "flex min-h-0 min-w-0 flex-1 flex-col bg-surface-contrast",
              isDashboardPage ? "overflow-hidden" : "overflow-y-auto",
              // The content area clips, and a page's first controls sit flush
              // on its top edge, where the focus ring drawn 2px outside them
              // would be cut. It reaches 2px up and pads back down, so content
              // keeps its place and the ring stays inside the clip. What it
              // reaches into is the header's bottom padding, on the same
              // surface, or the status banner's `mb-2` gap — this column is a
              // flex container, so the margins add rather than collapse and
              // the banner itself is never overlapped. A header with no bottom
              // padding has content (inline tabs) on its bottom edge, so the
              // content area doesn't reach up under it.
              !noPaddingBottom && "-mt-[2px] pt-[2px]",
              // Pages inset their content from the right on the 24px shell
              // gutter and leave the left to a docked sidebar's own padding;
              // with none docked, the shell supplies the left gutter so both
              // sides match.
              !isPanelDocked && "pl-6",
            )}
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  )
}

export default SidebarNav
